import {
  Component,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Canvas,
  useFrame,
  useLoader,
  useThree,
} from '@react-three/fiber';
import {
  Center,
  ContactShadows,
  Environment,
  OrbitControls,
  RoundedBox,
  useGLTF,
} from '@react-three/drei';
import {
  Box3,
  Vector3,
  CanvasTexture,
  DoubleSide,
  SRGBColorSpace,
  TextureLoader,
} from 'three';
import { QRCodeCanvas } from 'qrcode.react';
import {
  inferQrProductPreset,
  normalizePremiumDefinition,
} from './qrProductPresets';
import '../../../styles/QrPrint/QrProduct3DViewer.css';

const DEG_TO_RAD = Math.PI / 180;

const SCALE = 0.01;

const normalize = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const toScene = (value) =>
  Number(value || 0) * SCALE;

const clamp = (value, min, max) =>
  Math.min(
    max,
    Math.max(
      min,
      Number(value) || min,
    ),
  );

const getMaterialAppearance = ({
  material,
  finish,
  color,
  opacity = 1,
}) => {
  const normalizedMaterial =
    normalize(material);
  const normalizedFinish =
    normalize(finish);
  if (
    normalizedMaterial.includes('metal') ||
    normalizedMaterial.includes('aluminium') ||
    normalizedMaterial.includes('aluminum') ||
    normalizedMaterial.includes('steel') ||
    normalizedMaterial.includes('brass')
  ) {
    return {
      color,
      metalness: 0.88,
      roughness:
        normalizedFinish === 'matte'
          ? 0.44
          : normalizedFinish === 'frosted'
            ? 0.34
            : 0.2,
      transmission: 0,
      clearcoat: 0.35,
      clearcoatRoughness: 0.24,
      transparent: opacity < 1,
      opacity,
    };
  }
  if (
    normalizedMaterial.includes('wood') ||
    normalizedMaterial.includes('oak') ||
    normalizedMaterial.includes('walnut') ||
    normalizedMaterial.includes('bamboo')
  ) {
    return {
      color,
      metalness: 0,
      roughness:
        normalizedFinish === 'glossy'
          ? 0.34
          : 0.68,
      transmission: 0,
      clearcoat:
        normalizedFinish === 'glossy'
          ? 0.32
          : 0.06,
      clearcoatRoughness: 0.4,
      transparent: opacity < 1,
      opacity,
    };
  }
  if (
    normalizedMaterial.includes('acrylic') ||
    normalizedMaterial.includes('clear') ||
    normalizedMaterial.includes('frosted')
  ) {
    const frosted =
      normalizedFinish === 'frosted' ||
      normalizedMaterial.includes('frosted');
    return {
      color,
      metalness: 0,
      roughness:
        frosted
          ? 0.42
          : 0.12,
      transmission:
        frosted
          ? 0.26
          : 0.74,
      thickness: 0.35,
      ior: 1.46,
      clearcoat: 0.8,
      clearcoatRoughness: 0.1,
      transparent: true,
      opacity:
        opacity < 1
          ? opacity
          : frosted
            ? 0.86
            : 0.72,
    };
  }
  if (
    normalizedMaterial.includes('rubber')
  ) {
    return {
      color,
      metalness: 0,
      roughness: 0.92,
      transmission: 0,
      transparent: opacity < 1,
      opacity,
    };
  }
  return {
    color,
    metalness: 0.04,
    roughness:
      normalizedFinish === 'glossy'
        ? 0.28
        : 0.6,
    transmission: 0,
    clearcoat: 0.18,
    clearcoatRoughness: 0.42,
    transparent: opacity < 1,
    opacity,
  };
};

function LoadedPlane({
  textureUrl,
  width,
  height,
  position,
  rotation = [0, 0, 0],
}) {
  const texture = useLoader(
    TextureLoader,
    textureUrl,
  );
  useEffect(() => {
    texture.colorSpace =
      SRGBColorSpace;
    texture.needsUpdate = true;
  }, [texture]);
  return (
    <mesh
      position={position}
      rotation={rotation}
      renderOrder={5}
    >
      <planeGeometry
        args={[width, height]}
      />
      <meshBasicMaterial
        map={texture}
        transparent
        side={DoubleSide}
        depthWrite={false}
      />
    </mesh>
  );
}

function PremiumBlock({
  part,
  finish,
  colorOverride,
  materialOverride,
}) {
  const position =
    (part.position || [0, 0, 0])
      .map(toScene);
  const rotation =
    (part.rotation || [0, 0, 0])
      .map(
        (value) =>
          Number(value || 0) *
          DEG_TO_RAD,
      );
  const size =
    (part.size || [1, 1, 1])
      .map(toScene);
  const material =
    getMaterialAppearance({
      material:
        materialOverride ||
        part.material,
      finish,
      color:
        colorOverride ||
        part.color ||
        '#ffffff',
      opacity:
        part.opacity ?? 1,
    });
  if (part.type === 'cylinder') {
    const diameter = size[0];
    const height = size[1];
    return (
      <mesh
        position={position}
        rotation={rotation}
        castShadow
        receiveShadow
      >
        <cylinderGeometry
          args={[
            diameter / 2,
            diameter / 2,
            height,
            64,
          ]}
        />
        <meshPhysicalMaterial
          {...material}
        />
      </mesh>
    );
  }
  if (part.type === 'plane') {
    return (
      <mesh
        position={position}
        rotation={rotation}
      >
        <planeGeometry
          args={[
            size[0],
            size[1],
          ]}
        />
        <meshPhysicalMaterial
          {...material}
          side={DoubleSide}
        />
      </mesh>
    );
  }
  const radius =
    Math.max(
      0,
      Number(part.radius || 0),
    ) * SCALE;
  if (radius > 0) {
    return (
      <RoundedBox
        args={size}
        radius={Math.min(
          radius,
          Math.min(...size) / 2.4,
        )}
        smoothness={5}
        position={position}
        rotation={rotation}
        castShadow
        receiveShadow
      >
        <meshPhysicalMaterial
          {...material}
        />
      </RoundedBox>
    );
  }
  return (
    <mesh
      position={position}
      rotation={rotation}
      castShadow
      receiveShadow
    >
      <boxGeometry args={size} />
      <meshPhysicalMaterial
        {...material}
      />
    </mesh>
  );
}

const parseSizeScale = (selectedSize) => {
  const value = String(selectedSize || '').toLowerCase();
  if (!value) {
    return 1;
  }
  if (value.includes('a3')) {
    return 1.18;
  }
  if (value.includes('a4')) {
    return 1.08;
  }
  const match = value.match(/(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)/);
  if (!match) {
    return 1;
  }
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return 1;
  }
  const area = width * height;
  const baseArea = 10 * 15;
  return clamp(Math.sqrt(area / baseArea), 0.78, 1.32);
};

const applyLiveProductCustomization = ({
  definition,
  selectedMaterial,
  productColor,
  selectedSize,
  configuration,
}) => {
  const next =
    typeof structuredClone === 'function'
      ? structuredClone(definition)
      : JSON.parse(JSON.stringify(definition));
  const threeD = configuration?.three_d || {};
  const sizeScale = parseSizeScale(selectedSize);
  const thickness = Number(threeD.thickness);
  const baseStyle = threeD.base_style || 'classic';
  next.parts = (next.parts || []).map((part) => {
    const nextPart = {
      ...part,
      size: [...(part.size || [1, 1, 1])],
      position: [...(part.position || [0, 0, 0])],
      rotation: [...(part.rotation || [0, 0, 0])],
    };
    const role = nextPart.role || 'structure';
    const canChangeMaterial = [
      'base',
      'face',
      'support',
      'structure',
    ].includes(role);
    const canChangeColor = [
      'base',
      'face',
      'support',
      'structure',
    ].includes(role);
    if (canChangeMaterial && selectedMaterial) {
      nextPart.material = selectedMaterial;
    }
    if (canChangeColor && productColor) {
      nextPart.color = productColor;
      const normalizedMaterial = normalize(selectedMaterial);
      if (
        normalizedMaterial.includes('clear') ||
        normalizedMaterial.includes('acrylic')
      ) {
        nextPart.opacity =
          normalizedMaterial.includes('frosted')
            ? 0.82
            : 0.72;
      } else {
        nextPart.opacity = 1;
      }
    }
    if (Number.isFinite(thickness) && thickness > 0) {
      if (role === 'face' || role === 'support') {
        nextPart.size[2] = clamp(thickness, 3, 36);
      }
      if (role === 'base') {
        nextPart.size[1] = clamp(thickness * 0.9, 5, 32);
      }
    }
    if (role === 'base') {
      if (baseStyle === 'wide') {
        nextPart.size[0] *= 1.16;
        nextPart.size[2] *= 1.16;
        nextPart.size[1] *= 1.06;
      }
      if (baseStyle === 'minimal') {
        nextPart.size[0] *= 0.82;
        nextPart.size[2] *= 0.76;
        nextPart.size[1] *= 0.78;
      }
    }
    if (role !== 'base') {
      nextPart.size[0] *= sizeScale;
      nextPart.size[1] *= sizeScale;
      nextPart.position[1] *= sizeScale;
    }
    return nextPart;
  });
  const facePart =
    next.parts.find(
      (part) => part.role === 'face',
    ) ||
    next.parts.find(
      (part) => part.role === 'support',
    );
  if (facePart) {
    const faceFrontZ =
      Number(facePart.position?.[2] || 0) +
      Number(facePart.size?.[2] || 0) / 2;
    next.parts = next.parts.map((part) => {
      const isPanelAccent =
        part.role === 'accent' &&
        Number(part.position?.[1] || 0) > 50;
      if (
        part.role !== 'print_surface' &&
        !isPanelAccent
      ) {
        return part;
      }
      const adjusted = {
        ...part,
        position: [...(part.position || [0, 0, 0])],
      };
      if (part.role === 'print_surface') {
        adjusted.position[2] =
          faceFrontZ +
          Number(part.size?.[2] || 2) / 2 +
          1.2;
      }
      if (isPanelAccent) {
        adjusted.position[2] =
          faceFrontZ +
          Number(part.size?.[2] || 3) / 2 +
          2.2;
      }
      return adjusted;
    });
  }
  return next;
};

function PremiumProduct({
  definition,
  configuration,
  designTextureUrl,
  selectedMaterial,
  selectedSize,
  productColor,
}) {
  const finish =
    configuration?.three_d
      ?.surface_finish ||
    'glossy';
  const accentColor =
    configuration?.accent_color ||
    '#51766e';
  const liveDefinition =
    applyLiveProductCustomization({
      definition,
      selectedMaterial,
      selectedSize,
      productColor,
      configuration,
    });
  const printPart =
    liveDefinition.parts?.find(
      (part) =>
        part.role ===
        'print_surface',
    );
  const facePart =
    printPart ||
    liveDefinition.parts?.find(
      (part) =>
        part.role === 'face',
    );
  const designRotation =
    (facePart?.rotation ||
      [0, 0, 0])
      .map(
        (value) =>
          Number(value || 0) *
          DEG_TO_RAD,
      );
  const designPosition =
    facePart
      ? [
          toScene(
            facePart.position?.[0] ||
              0,
          ),
          toScene(
            facePart.position?.[1] ||
              0,
          ),
          toScene(
            facePart.position?.[2] ||
              0,
          ) +
            toScene(
              Math.min(
                facePart.size?.[2] ||
                  2,
                12,
              ),
            ) /
              2 +
            0.006,
        ]
      : [0, 1, 0.05];
  const designWidth =
    toScene(
      facePart?.size?.[0] || 104,
    ) * 0.93;
  const designHeight =
    toScene(
      facePart?.size?.[1] || 146,
    ) * 0.93;
  const standAngle = Number(
    configuration?.three_d?.stand_angle,
  );
  const panelExtraRotation =
    Number.isFinite(standAngle)
      ? (8 - clamp(standAngle, 0, 32)) *
        DEG_TO_RAD
      : 0;
  const isPanelPart = (part) => {
    const role = part.role || 'structure';
    if (
      role === 'face' ||
      role === 'print_surface' ||
      role === 'support'
    ) {
      return true;
    }
    return (
      role === 'accent' &&
      Number(part.position?.[1] || 0) > 50
    );
  };
  const renderPart = (part) => {
    const override =
      part.role === 'accent'
        ? accentColor
        : undefined;
    return (
      <PremiumBlock
        key={part.id}
        part={part}
        finish={finish}
        colorOverride={override}
        materialOverride={
          [
            'base',
            'face',
            'support',
            'structure',
          ].includes(part.role)
            ? selectedMaterial
            : undefined
        }
      />
    );
  };
  return (
    <group
      position={[
        0,
        -0.95,
        0,
      ]}
    >
      {liveDefinition.parts
        ?.filter((part) => !isPanelPart(part))
        .map(renderPart)}
      <group
        rotation={[
          panelExtraRotation,
          0,
          0,
        ]}
      >
        {liveDefinition.parts
          ?.filter(isPanelPart)
          .map(renderPart)}
        {designTextureUrl &&
        facePart ? (
          <LoadedPlane
            textureUrl={designTextureUrl}
            width={designWidth}
            height={designHeight}
            position={designPosition}
            rotation={designRotation}
          />
        ) : null}
      </group>
    </group>
  );
}

// Prefer an actual GLB URL over potentially stale `three_d_source` metadata.
// Preserve Laravel `/index.php/storage/...` paths unchanged.
const getUploadedModelUrl = (product) => {
  const candidates = [
    product?.three_d_model_url,
    product?.threeDModelUrl,
    product?.model_url,
    product?.modelUrl,
    product?.three_d_model?.url,
    product?.model?.url,
  ];
  return candidates.find((value) => typeof value === 'string' && value.trim())?.trim() || '';
};

/** GLTFLoader rejects on network/CORS failures; Suspense alone does not catch errors. */

class ModelLoadBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error) {
    console.error('QR Print: unable to load GLB:', this.props.url, error);
    this.props.onError?.(error);
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function UploadedModel({
  url,
  definition,
  designTextureUrl,
}) {
  const gltf = useGLTF(url);
  const normalizedScene = useMemo(() => {
    const scene = gltf.scene.clone(true);
    // GLBs can be exported in metres, centimetres or millimetres;
    // normalize their bounding box so the object stays in camera view.
    const bounds = new Box3().setFromObject(scene);
    const dimensions = bounds.getSize(new Vector3());
    const maxDimension = Math.max(dimensions.x, dimensions.y, dimensions.z);
    if (Number.isFinite(maxDimension) && maxDimension > 0) {
      scene.scale.multiplyScalar(2 / maxDimension);
    }
    return scene;
  }, [gltf.scene]);
  const frontZone =
    definition
      ?.customization_zones
      ?.find(
        (zone) =>
          zone.type === 'qr',
      );
  const position =
    frontZone?.position ||
    [0, 0.55, 0.52];
  const size =
    frontZone?.size ||
    [0.72, 0.72];
  return (
    <group>
      <Center>
        <primitive
          object={normalizedScene}
        />
      </Center>
      {designTextureUrl ? (
        <LoadedPlane
          textureUrl={
            designTextureUrl
          }
          width={
            Number(size[0]) ||
            0.72
          }
          height={
            Number(size[1]) ||
            0.72
          }
          position={[
            Number(position[0]) ||
              0,
            Number(position[1]) ||
              0.55,
            Number(position[2]) ||
              0.52,
          ]}
        />
      ) : null}
    </group>
  );
}

function PreviewCapture({
  enabled,
  onPreviewReady,
}) {
  const {
    gl,
    scene,
    camera,
  } = useThree();
  const frameCount = useRef(0);
  const captured = useRef(false);
  useFrame(() => {
    if (
      !enabled ||
      captured.current ||
      !onPreviewReady
    ) {
      return;
    }
    frameCount.current += 1;
    if (frameCount.current < 24) {
      return;
    }
    try {
      gl.render(scene, camera);
      const image =
        gl.domElement.toDataURL(
          'image/png',
          0.92,
        );
      captured.current = true;
      onPreviewReady(image);
    } catch {
      // Preview capture is best-effort.
    }
  });
  useEffect(() => {
    captured.current = false;
    frameCount.current = 0;
  }, [
    enabled,
    onPreviewReady,
  ]);
  return null;
}

function ProductScene({
  product,
  selectedMaterial,
  selectedSize,
  productColor,
  configuration,
  designTextureUrl,
}) {
  const uploadedModelUrl = getUploadedModelUrl(product);
  const presetKey =
    product
      ?.three_d_definition
      ?.preset_key ||
    inferQrProductPreset({
      productType:
        product?.product_type,
      material:
        selectedMaterial,
    });
  const definition =
    normalizePremiumDefinition(
      product
        ?.three_d_definition,
      presetKey,
    );
  return (
    <>
      <ambientLight
        intensity={0.55}
      />
      <directionalLight
        position={[4.5, 6.5, 4.5]}
        intensity={1.35}
        castShadow
      />
      <directionalLight
        position={[-4, 3, 2]}
        intensity={0.6}
      />
      <pointLight
        position={[0, 4, -3]}
        intensity={0.35}
      />
      <Environment
        preset="studio"
      />
      {uploadedModelUrl ? (
        <ModelLoadBoundary
          key={uploadedModelUrl}
          url={uploadedModelUrl}
          fallback={
            <PremiumProduct
              definition={definition}
              configuration={configuration}
              selectedMaterial={selectedMaterial}
              selectedSize={selectedSize}
              productColor={productColor}
              designTextureUrl={designTextureUrl}
            />
          }
        >
        <UploadedModel
          url={uploadedModelUrl}
          definition={
            product
              .three_d_definition
          }
          designTextureUrl={
            designTextureUrl
          }
        />
        </ModelLoadBoundary>
      ) : (
        <PremiumProduct
          definition={definition}
          configuration={
            configuration
          }
          selectedMaterial={
            selectedMaterial
          }
          selectedSize={
            selectedSize
          }
          productColor={
            productColor
          }
          designTextureUrl={
            designTextureUrl
          }
        />
      )}
      <ContactShadows
        position={[
          0,
          -1.12,
          0,
        ]}
        opacity={0.28}
        scale={5}
        blur={2.7}
        far={4}
      />
      <OrbitControls
        makeDefault
        enablePan={false}
        minDistance={2.5}
        maxDistance={6.5}
        minPolarAngle={0.85}
        maxPolarAngle={1.7}
        autoRotate={
          configuration
            ?.three_d
            ?.auto_rotate === true
        }
        autoRotateSpeed={1.05}
      />
    </>
  );
}

const getDesignVariant = (
  configuration,
) => {
  const value =
    configuration
      ?.three_d
      ?.design_variant;
  if (
    [
      'editorial',
      'minimal',
      'luxury',
      'bold',
    ].includes(value)
  ) {
    return value;
  }
  return 'editorial';
};

const drawRoundedRect = (
  ctx,
  x,
  y,
  width,
  height,
  radius,
) => {
  const r = Math.min(
    radius,
    width / 2,
    height / 2,
  );
  ctx.beginPath();
  ctx.roundRect(
    x,
    y,
    width,
    height,
    r,
  );
};

const loadDesignImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const image = new Image();
    if (!String(src).startsWith('data:')) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

const drawDesignTexture = async ({
  qrCanvas,
  restaurantName,
  customText,
  backgroundColor,
  textColor,
  accentColor,
  qrBackground,
  variant,
  editorSchema,
  editorElements,
}) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 1400;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }
  const bg = backgroundColor || '#ffffff';
  const text = textColor || '#1f2937';
  const accent = accentColor || '#51766e';
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const blocks =
    editorSchema?.blocks?.filter(
      (block) => block?.enabled !== false,
    ) || [];
  if (blocks.length > 0) {
    const elements = editorElements || {};
    blocks
      .filter((block) => block.type === 'text')
      .forEach((block) => {
        const value =
          elements[block.id] ||
          block.default ||
          {};
        const fallbackText =
          block.id === 'restaurant-name'
            ? restaurantName
            : block.id === 'call-to-action'
              ? customText
              : '';
        const content =
          value.text || fallbackText || '';
        if (!content) {
          return;
        }
        const x =
          (Number(value.x ?? 50) / 100) *
          canvas.width;
        const y =
          (Number(value.y ?? 50) / 100) *
          canvas.height;
        const fontSize = Math.max(
          20,
          Math.min(
            140,
            Number(value.font_size || 4) * 14,
          ),
        );
        ctx.textAlign = value.align || 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = value.color || text;
        ctx.font = `${Number(
          value.weight || 700,
        )} ${fontSize}px Arial, sans-serif`;
        ctx.fillText(
          String(content).slice(
            0,
            block.settings?.max_length || 120,
          ),
          x,
          y,
          canvas.width * 0.86,
        );
      });
    const imageBlocks = blocks.filter(
      (block) =>
        block.type === 'logo' ||
        block.type === 'image',
    );
    for (const block of imageBlocks) {
      const value =
        elements[block.id] ||
        block.default ||
        {};
      if (!value.src) {
        continue;
      }
      const image =
        await loadDesignImage(value.src);
      if (!image) {
        continue;
      }
      const x =
        (Number(value.x ?? 50) / 100) *
        canvas.width;
      const y =
        (Number(value.y ?? 50) / 100) *
        canvas.height;
      const width =
        (Number(value.width || 24) / 100) *
        canvas.width;
      const ratio =
        image.naturalWidth > 0
          ? image.naturalHeight / image.naturalWidth
          : 1;
      const height = width * ratio;
      ctx.save();
      ctx.globalAlpha =
        Number(value.opacity ?? 1);
      ctx.drawImage(
        image,
        x - width / 2,
        y - height / 2,
        width,
        height,
      );
      ctx.restore();
    }
    const qrBlock = blocks.find(
      (block) => block.type === 'qr',
    );
    if (qrBlock) {
      const value =
        elements[qrBlock.id] ||
        qrBlock.default ||
        {};
      const x =
        (Number(value.x ?? 50) / 100) *
        canvas.width;
      const y =
        (Number(value.y ?? 50) / 100) *
        canvas.height;
      const size = Math.max(
        160,
        Math.min(
          700,
          (Number(value.size || 34) / 100) *
            canvas.width,
        ),
      );
      const left = x - size / 2;
      const top = y - size / 2;
      ctx.save();
      ctx.fillStyle =
        value.background_color ||
        qrBackground ||
        '#ffffff';
      ctx.shadowColor = 'rgba(15,23,42,0.14)';
      ctx.shadowBlur = 26;
      ctx.shadowOffsetY = 10;
      drawRoundedRect(
        ctx,
        left - 20,
        top - 20,
        size + 40,
        size + 40,
        34,
      );
      ctx.fill();
      ctx.restore();
      ctx.drawImage(
        qrCanvas,
        left,
        top,
        size,
        size,
      );
    }
    return canvas.toDataURL('image/png');
  }
  if (variant === 'luxury') {
    ctx.fillStyle = accent;
    ctx.fillRect(0, 0, canvas.width, 28);
    ctx.fillStyle = 'rgba(0,0,0,0.04)';
    ctx.fillRect(72, 105, 880, 1190);
  } else if (variant === 'bold') {
    ctx.fillStyle = accent;
    ctx.fillRect(0, 0, canvas.width, 210);
  } else if (variant === 'editorial') {
    ctx.fillStyle = accent;
    ctx.fillRect(88, 90, 150, 14);
  }
  ctx.textAlign =
    variant === 'bold' ? 'center' : 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle =
    variant === 'bold' ? '#ffffff' : text;
  ctx.font = '700 58px Arial, sans-serif';
  const titleX = variant === 'bold' ? 512 : 92;
  const titleY = variant === 'bold' ? 108 : 178;
  ctx.fillText(
    String(
      restaurantName || 'Restaurant',
    ).slice(0, 34),
    titleX,
    titleY,
    840,
  );
  const qrCardSize =
    variant === 'minimal' ? 520 : 560;
  const qrCardX = (1024 - qrCardSize) / 2;
  const qrCardY =
    variant === 'bold' ? 310 : 350;
  ctx.save();
  ctx.shadowColor = 'rgba(15,23,42,0.16)';
  ctx.shadowBlur = 34;
  ctx.shadowOffsetY = 16;
  ctx.fillStyle = qrBackground || '#ffffff';
  drawRoundedRect(
    ctx,
    qrCardX,
    qrCardY,
    qrCardSize,
    qrCardSize,
    48,
  );
  ctx.fill();
  ctx.restore();
  ctx.drawImage(
    qrCanvas,
    qrCardX + 54,
    qrCardY + 54,
    qrCardSize - 108,
    qrCardSize - 108,
  );
  ctx.textAlign = 'center';
  ctx.fillStyle = text;
  ctx.font = '700 42px Arial, sans-serif';
  ctx.fillText(
    String(
      customText || 'Scan to view our menu',
    ).slice(0, 70),
    512,
    1050,
    780,
  );
  return canvas.toDataURL('image/png');
};

export default function QrProduct3DViewer({
  product,
  menuUrl,
  configuration,
  selectedMaterial,
  selectedSize,
  productColor,
  restaurantName,
  capturePreview = false,
  onPreviewReady,
  compact = false,
}) {
  const qrCanvasRef =
    useRef(null);
  const [
    designTextureUrl,
    setDesignTextureUrl,
  ] = useState('');
  const qrForeground =
    configuration?.qr
      ?.foreground_color ||
    configuration?.qr
      ?.foreground ||
    '#111111';
  const qrBackground =
    configuration?.qr
      ?.background_color ||
    configuration?.qr
      ?.background ||
    '#ffffff';
  const designVariant =
    getDesignVariant(
      configuration,
    );
  useEffect(() => {
    const timer =
      window.setTimeout(
        async () => {
          const qrCanvas =
            qrCanvasRef.current;
          if (!qrCanvas) {
            return;
          }
          const nextTextureUrl =
            await drawDesignTexture({
              qrCanvas,
              restaurantName,
              customText:
                configuration
                  ?.custom_text,
              backgroundColor:
                configuration
                  ?.background_color,
              textColor:
                configuration
                  ?.text_color,
              accentColor:
                configuration
                  ?.accent_color,
              qrBackground,
              variant:
                designVariant,
              editorSchema:
                product?.editor_schema,
              editorElements:
                configuration?.editor_elements,
            });
          setDesignTextureUrl(nextTextureUrl);
        },
        40,
      );
    return () => {
      window.clearTimeout(
        timer,
      );
    };
  }, [
    configuration
      ?.accent_color,
    configuration
      ?.background_color,
    configuration
      ?.custom_text,
    configuration
      ?.text_color,
    designVariant,
    menuUrl,
    qrBackground,
    qrForeground,
    restaurantName,
    product?.editor_schema,
    configuration?.editor_elements,
  ]);
  const presetKey =
    product
      ?.three_d_definition
      ?.preset_key ||
    inferQrProductPreset({
      productType:
        product?.product_type,
      material:
        selectedMaterial,
    });
  return (
    <div
      className={`qr-print-3d-viewer qr-print-3d-viewer--${presetKey}${
        compact ? ' qr-print-3d-viewer--compact' : ''
      }`}
    >
      <div
        className="qr-print-3d-qr-source"
        aria-hidden="true"
      >
        <QRCodeCanvas
          ref={qrCanvasRef}
          value={menuUrl}
          size={640}
          level="H"
          marginSize={clamp(
            configuration
              ?.qr
              ?.margin ||
              4,
            4,
            12,
          )}
          bgColor={
            qrBackground
          }
          fgColor={
            qrForeground
          }
        />
      </div>
      <div className="qr-print-3d-canvas-wrap">
        {designTextureUrl ? (
          <Canvas
            shadows
            style={{
              width: '100%',
              height: '100%',
            }}
            dpr={[1, 1.8]}
            gl={{
              preserveDrawingBuffer:
                capturePreview,
              antialias: true,
            }}
            camera={{
              position: [
                2.7,
                1.8,
                3.55,
              ],
              fov: 34,
            }}
          >
            <color
              attach="background"
              args={[
                '#aab8c9',
              ]}
            />
            <Suspense
              fallback={null}
            >
              <ProductScene
                product={
                  product
                }
                selectedMaterial={
                  selectedMaterial
                }
                selectedSize={
                  selectedSize
                }
                productColor={
                  productColor
                }
                configuration={
                  configuration
                }
                designTextureUrl={
                  designTextureUrl
                }
              />
              <PreviewCapture
                enabled={capturePreview}
                onPreviewReady={
                  onPreviewReady
                }
              />
            </Suspense>
          </Canvas>
        ) : (
          <div className="qr-print-3d-loading">
            <span />
          </div>
        )}
      </div>
      {!compact ? (
      <div className="qr-print-3d-viewer-footer">
        <span>
          {selectedMaterial ||
            'Premium material'}
        </span>
        <span>
          {selectedSize ||
            'Custom size'}
        </span>
        <span>
          {getUploadedModelUrl(product)
            ? 'Uploaded GLB'
            : 'Premium block system'}
        </span>
      </div>
      ) : null}
    </div>
  );
}
